import React, { useState } from 'react';
import API from '../../../../utils/api';
import Iconify from '../../../Iconify';
import usePaymentStore from '../../shared/store/use-payment-store';
import { showModalError } from '../../shared/components/modals/modal-error';
import { useCopyClipboard } from '../../shared/hooks/use-copy-clipboard';
import { waitUserCancelCashoutsPending } from '../../shared/utils/cashouts';
import styles from './spei.module.css';
import iconoGeneraReferencia from '../../shared/assets/icono-genera-referencia.png';
import iconoBotonObtenerReferencia from '../../shared/assets/icono-boton-obtener-referencia.png';
import iconoHeader from '../../shared/assets/icono-usa-esta-info-para-tu-transf.png';
import iconoTarjeta from '../../shared/assets/icono-tarjeta.png';
import iconoBanco from '../../shared/assets/icono-banco.png';
import iconoConcepto from '../../shared/assets/icono-concepto.png';
import { InstructionsSpei } from '../../shared/components/instructions';
import { SpeiWarnings } from '../../shared/components/spei-warnings';
import { waitVerificationName } from '../../shared/components/modals/modal-verification-name';

export const SpeiMuwe = () => {
  const { amount, isInvalidAmount } = usePaymentStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const { copyToClipboard, copiedField } = useCopyClipboard();

  const processPayment = async () => {
    setIsSubmitting(true);

    try {
      const userCancell = await waitUserCancelCashoutsPending({
        buttonConfirm: 'Generar Referencia',
        buttonCancel: 'Cancelar',
      });

      if (userCancell) {
        setIsSubmitting(false);
        return;
      }

      const data = {
        amount: 'other',
        other_amount: amount,
        method: 'spei',
      };

      const response = await API.createDepositOrderMuwe(data);

      if (response?.data?.success) {
        setOrder(response.data.order);
      } else {
        const errorMessage = response?.data?.error || 'Ha ocurrido un error';
        showModalError({ errorMessage });
      }
    } catch (error) {
      showModalError({ errorMessage: 'Ha ocurrido un error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      {!order && (
        <div className={styles.container}>
          {/* Header con icono y título */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <img src={iconoGeneraReferencia} alt="Icono generar referencia" />
            </div>
            <h2 className={styles.headerTitle}>
              Presiona sobre el botón para generar la referencia{' '}
              <span className={styles.headerTitleHighlight}>para tu transferencia.</span>
            </h2>
          </div>

          {/* Botón de acción */}
          <div className={styles.buttonContainer}>
            <button
              className="payment-button"
              onClick={processPayment}
              disabled={isSubmitting || isInvalidAmount}
            >
              <span className={styles.buttonText}>{isSubmitting ? 'Generando referencia...' : 'Obtener referencia'}</span>
              <img
                src={iconoBotonObtenerReferencia}
                alt="Icono obtener referencia"
                className={styles.buttonIcon}
              />
            </button>
          </div>

          {/* Información importante */}
          <div className={styles.infoBox}>
            <p className={styles.infoText}>
              Para que tu transferencia se complete con éxito asegúrate de llenar correctamente la
              cuenta <strong>CLABE, referencia y concepto.</strong>
            </p>
          </div>

          <SpeiWarnings />
        </div>
      )}

      {order && (
        <div className={styles.container}>
          {/* Header con icono y título */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <img src={iconoHeader} alt="Icono de información" />
            </div>
            <h2 className={styles.headerTitle}>
              Usa esta información para{' '}
              <span className={styles.headerTitleHighlight}>hacer tu transferencia</span>
            </h2>
          </div>

          <InstructionsSpei />

          {/* Campos de información */}
          <div className={styles.fieldsContainer}>
            {/* Campo Banco */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Banco</label>
              <div
                className={`${styles.fieldInput} ${
                  copiedField === 'banco' ? styles.fieldInputCopied : ''
                }`}
                onClick={() => copyToClipboard('ARCUS', 'banco')}
              >
                <img src={iconoBanco} alt="Icono banco" className={styles.fieldIcon} />
                <span className={styles.fieldValue}>ARCUS</span>
                {copiedField === 'banco' ? (
                  <span className={styles.copiedFeedback}>
                    <Iconify icon="material-symbols:check-circle" className={styles.copiedIcon} />
                    <span className={styles.copiedText}>¡Copiado!</span>
                  </span>
                ) : (
                  <Iconify icon="material-symbols:content-copy" className={styles.copyIcon} />
                )}
              </div>
            </div>

            {/* Campo CLABE */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Cuenta CLABE</label>
              <div
                className={`${styles.fieldInput} ${
                  copiedField === 'clabe' ? styles.fieldInputCopied : ''
                }`}
                onClick={() => copyToClipboard(order.reference, 'clabe')}
              >
                <img src={iconoTarjeta} alt="Icono tarjeta" className={styles.fieldIcon} />
                <span className={styles.fieldValue}>{order.reference}</span>
                {copiedField === 'clabe' ? (
                  <span className={styles.copiedFeedback}>
                    <Iconify icon="material-symbols:check-circle" className={styles.copiedIcon} />
                    <span className={styles.copiedText}>¡Copiado!</span>
                  </span>
                ) : (
                  <Iconify icon="material-symbols:content-copy" className={styles.copyIcon} />
                )}
              </div>
            </div>

            {/* Campo Concepto */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Concepto</label>
              <div
                className={`${styles.fieldInput} ${
                  copiedField === 'concepto' ? styles.fieldInputCopied : ''
                }`}
                onClick={() => copyToClipboard('DEPOSITO PICKWIN', 'concepto')}
              >
                <img src={iconoConcepto} alt="Icono concepto" className={styles.fieldIcon} />
                <span className={styles.fieldValue}>DEPOSITO PICKWIN</span>
                {copiedField === 'concepto' ? (
                  <span className={styles.copiedFeedback}>
                    <Iconify icon="material-symbols:check-circle" className={styles.copiedIcon} />
                    <span className={styles.copiedText}>¡Copiado!</span>
                  </span>
                ) : (
                  <Iconify icon="material-symbols:content-copy" className={styles.copyIcon} />
                )}
              </div>
            </div>
          </div>

          <SpeiWarnings />

          {/* Botón para generar otra referencia */}
          <div className={styles.buttonContainer}>
            <button
              className="payment-button"
              onClick={processPayment}
              disabled={isSubmitting || isInvalidAmount}
            >
              <span className={styles.buttonText}>
                {isSubmitting
                  ? 'Generando referencia...'
                  : 'Generar otra referencia'}
              </span>
              {!isSubmitting && (
                <img
                  src={iconoBotonObtenerReferencia}
                  alt="Icono obtener referencia"
                  className={styles.buttonIcon}
                />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
